import React from 'react';
import { isNil } from 'lodash';
import lowerCase from 'lodash/lowerCase';
import upperFirst from 'lodash/upperFirst';
import { Box, Text } from '@atlaskit/primitives';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { BillingDocWithLineItems, Refund } from '@atlassian/cssxp-types-billing';
import { BillingContactAddress, SourceSystem } from '@css-xp/graphtypes/src/generated-types-new';
import {
  FormattedDate,
  SummaryPanelContainer,
  SummaryPanelIcon,
  SummaryPanelRow,
  FormattedAddress,
  CustomPanel,
  CopyableText,
} from '@css-xp/base';
import { SoldToAddressDetailsLink, NoData } from '@css-xp/ppc-common';
import { TaxComponent } from '@css-xp/ppc-tax-component';
import { RefundPaidDate } from '../refund-paid-date';
import './refund-details-summary-section.scss';

function getFormattedAddress(address: BillingContactAddress, fullName: string | null) {
  return (
    <FormattedAddress
      addressLine1={address?.address1}
      addressLine2={address?.address2}
      city={address?.city}
      country={address?.country}
      postcode={address?.postalCode}
      state={address?.state}
      organizationName={fullName}
    />
  );
}

export const RefundDetailSummarySection = ({
  billingDoc,
  transactionAccountId,
  isDesignIteration1Enabled,
}: {
  billingDoc?: BillingDocWithLineItems;
  transactionAccountId?: string;
  isDesignIteration1Enabled?: boolean;
}) => {
  const refund = billingDoc as Refund;

  if (!billingDoc) {
    return null;
  }

  // refund.sourceSystem is in lower case due to which the conditional blocks were skipped
  const refundSourceSystem = refund?.sourceSystem?.toUpperCase();

  const getSoldToAddress = () => {
    const fullName = billingDoc?.shipTo?.fullName as string | null;
    const renderTaxComponent = () => {
      return (
        <TaxComponent
          billToAddress={billingDoc?.shipTo}
          gridItemClassName="tax-component-section"
          hasSeparator={true}
        />
      );
    };

    if (isDesignIteration1Enabled) {
      return (
        <div>
          <Text as="p" weight="semibold">
            {fullName}
          </Text>
          <div className={fullName ? 'pv-8' : 'pb-8'}>
            <span>
              {billingDoc?.shipTo?.address?.address1 ? (
                getFormattedAddress(billingDoc?.shipTo?.address, null)
              ) : (
                <p>Same as billing address</p>
              )}
            </span>
          </div>
          {renderTaxComponent()}
        </div>
      );
    }

    return (
      <div>
        <SoldToAddressDetailsLink
          txaId={transactionAccountId}
          //@ts-ignore
          shipToId={billingDoc?.shipTo?.id}
        >
          <div className="customer-address-section">
            <span className="line-clamp">
              {billingDoc?.shipTo?.address?.address1 ? (
                getFormattedAddress(billingDoc?.shipTo?.address, fullName)
              ) : (
                <p>Same as billing address</p>
              )}
            </span>
          </div>
        </SoldToAddressDetailsLink>
        {renderTaxComponent()}
      </div>
    );
  };

  const renderReasonRow = (refund: Refund) => {
    const reason = refund?.reason ? upperFirst(lowerCase(refund.reason)) : <NoData />;
    const label = 'Reason';

    if (isDesignIteration1Enabled) {
      const reasonCode = refund.reasonCode as string;
      const reasonCodeValue =
        refund.sourceSystem === (SourceSystem.HAMS as string) && upperFirst(lowerCase(reasonCode));

      return (
        <SummaryPanelRow
          label={label}
          value={
            <>
              <p>{reason}</p>
              <Text color="color.text.subtle">{reasonCodeValue}</Text>
            </>
          }
          useWrapper
        />
      );
    }

    return <SummaryPanelRow label={label} value={reason} />;
  };

  const renderPONumberRow = () => {
    if (refundSourceSystem !== SourceSystem.CCP) {
      return null;
    }

    const label = 'PO number';

    if (isDesignIteration1Enabled) {
      return (
        <SummaryPanelRow
          label={label}
          value={!isNil(refund.purchaseOrderNumber) ? refund?.purchaseOrderNumber : <NoData />}
          useWrapper
        />
      );
    }

    return (
      <SummaryPanelRow
        label={label}
        value={!isNil(refund.purchaseOrderNumber) ? refund?.purchaseOrderNumber : 'Not added'}
      />
    );
  };

  const renderTransactionIdRow = () => {
    if (refundSourceSystem !== SourceSystem.CCP) {
      return null;
    }

    if (isDesignIteration1Enabled) {
      const copyableValue = <CopyableText text={refund?.arn} />;

      return (
        <SummaryPanelRow label="Transaction ID" value={!isNil(refund?.arn) ? copyableValue : <NoData />} useWrapper />
      );
    }

    return <SummaryPanelRow label="Transaction id" value={!isNil(refund?.arn) ? refund?.arn : 'Not available'} />;
  };

  const renderBillToAddressRow = () => {
    const address1 = billingDoc?.billTo?.address?.address1;
    const fullName = billingDoc?.billTo?.fullName;
    const renderTaxComponent = () => {
      return (
        <TaxComponent
          billToAddress={billingDoc?.billTo}
          gridItemClassName="tax-component-section"
          hasSeparator={true}
        />
      );
    };

    if (isDesignIteration1Enabled) {
      const value = (
        <div>
          <Text as="p" weight="semibold">
            {fullName}
          </Text>
          <p className={fullName ? 'pv-8' : 'pb-8'}>
            {address1 ? getFormattedAddress(billingDoc?.billTo?.address as BillingContactAddress, null) : <NoData />}
          </p>
          {renderTaxComponent()}
        </div>
      );
      return <SummaryPanelRow label="Billing address" value={value} useWrapper />;
    }

    const value = (
      <div className="customer-address-section">
        <span className="line-clamp">
          {address1 ? (
            getFormattedAddress(billingDoc?.billTo?.address as BillingContactAddress, fullName as string | null)
          ) : (
            <NoData />
          )}
        </span>
        {renderTaxComponent()}
      </div>
    );

    return <SummaryPanelRow label="Bill-to address" value={value} />;
  };

  const renderSoldToAddressRow = () => {
    return (
      <SummaryPanelRow label="Sold-to address" value={getSoldToAddress()} useWrapper={isDesignIteration1Enabled} />
    );
  };

  if (isDesignIteration1Enabled) {
    const accordionTitle = 'Refund details';

    return (
      <Box testId="refund-details-summary-section-v2">
        <CustomPanel expandedHeader={accordionTitle} collapsedHeader={accordionTitle} version={2} isExpanded>
          <Box padding="space.250" paddingBlockStart="space.150">
            <SummaryPanelContainer version={2}>
              {renderTransactionIdRow()}
              {renderPONumberRow()}
              {renderReasonRow(refund)}
              {renderSoldToAddressRow()}
              {renderBillToAddressRow()}
            </SummaryPanelContainer>
          </Box>
        </CustomPanel>
      </Box>
    );
  }

  return (
    <div className="refund-details-summary-section">
      <SummaryPanelContainer title="Refund details" iconKey={SummaryPanelIcon.SUBSCRIPTION_ICON}>
        {renderBillToAddressRow()}
        {renderSoldToAddressRow()}
      </SummaryPanelContainer>
      <SummaryPanelContainer title="Billing details" iconKey={SummaryPanelIcon.BILLING_PAYMENT_ICON}>
        <SummaryPanelRow
          label="Issued"
          value={<FormattedDate date={refund.createdDate} showTime={true} format={NEW_DATE_FORMAT} />}
        />
        {renderPONumberRow()}
        {renderTransactionIdRow()}
        {
          <>
            {renderReasonRow(refund)}
            <RefundPaidDate refund={refund} />
          </>
        }
      </SummaryPanelContainer>
    </div>
  );
};
